import { Component, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { FieldTree, FormField } from '@angular/forms/signals';
import {
  TConfirmationModalData,
  TIconMetaData,
  TJunoSelectOption,
  SigningMethodEnum,
  VerificationMethod,
} from '@dss-libs/shared/type/common-types';
import { TranslocoDirective, translateSignal } from '@jsverse/transloco';
import { TranslocoDecimalPipe, TranslocoLocaleService } from '@jsverse/transloco-locale';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import {
  ButtonComponent,
  ConfirmationModalComponent,
  JunoCheckbox,
  JunoInput,
  JunoSelect,
} from '@dss-libs/shared/ui/common-components';
import { ModalUiService } from '@dss-libs/shared/utility/common-services';
import {
  IOrderSettingsFormModel,
  TNotificationRecipientField,
  IProviderMethod,
  ReminderInterval,
  ReminderIntervalTranslationMapper,
  VerificationMethodTranslationMapper,
} from '@dss-libs/portal/type/portal-types';
import { createEmptyNotificationRecipient } from '../forms/create-order-form.factory';

@Component({
  selector: 'dss-libs-feature-order-settings',
  imports: [
    FormField,
    TranslocoDirective,
    TranslocoDecimalPipe,
    MatIcon,
    MatIconButton,
    JunoInput,
    JunoSelect,
    ButtonComponent,
    JunoCheckbox,
    NgTemplateOutlet,
  ],
  templateUrl: './order-settings.component.html',
  styleUrl: './order-settings.component.css',
})
export class OrderSettingsComponent {
  readonly #modalService = inject(ModalUiService);
  readonly #localeService = inject(TranslocoLocaleService);
  readonly #locale = toSignal(this.#localeService.localeChanges$, { initialValue: this.#localeService.getLocale() });
  readonly #dayLabel = translateSignal('DAY');
  readonly #daysLabel = translateSignal('DAYS');

  readonly addReceiverIcon: TIconMetaData = { icon: 'user-plus-rounded-bold' };

  readonly form = input.required<FieldTree<IOrderSettingsFormModel>>();
  readonly remindingPeriods = input<ReminderInterval[]>([]);
  readonly expirationPeriods = input<number[]>([]);
  readonly signingMethods = input<IProviderMethod[]>([]);

  protected readonly doodleSigningMethod = SigningMethodEnum.DOODLE;
  readonly expandedReceivers = signal<ReadonlySet<TNotificationRecipientField>>(new Set());

  protected readonly languageOptions: TJunoSelectOption<string>[] = [
    { value: 'en', label: 'ENGLISH' },
    { value: 'ar', label: 'ARABIC' },
  ];

  protected readonly verificationMethodOptions: TJunoSelectOption<string>[] = Object.values(VerificationMethod).map(
    (method) => ({ value: method, label: VerificationMethodTranslationMapper.get(method) ?? '' })
  );

  protected readonly reminderIntervalOptions = computed<TJunoSelectOption<string>[]>(() =>
    this.remindingPeriods().map((period) => ({
      value: period,
      label: ReminderIntervalTranslationMapper.get(period) ?? '',
    }))
  );

  protected readonly expirationPeriodOptions = computed<TJunoSelectOption<string>[]>(() => {
    const locale = this.#locale();
    return this.expirationPeriods().map((period) => ({
      value: String(period),
      label: `${this.#localeService.localizeNumber(period, 'decimal', locale)} ${
        period === 1 ? this.#dayLabel() : this.#daysLabel()
      }`,
    }));
  });

  protected readonly signingMethodOptions = computed<TJunoSelectOption<string>[]>(() => [
    { value: SigningMethodEnum.DEFAULT, label: 'CONTRACT_ORDER.NOT_SPECIFIED' },
    ...this.signingMethods().map(({ providerName }) => ({ value: providerName, label: providerName })),
  ]);

  toggleReceiver(receiver: TNotificationRecipientField): void {
    const receivers = new Set(this.expandedReceivers());
    if (receivers.has(receiver)) {
      receivers.delete(receiver);
    } else {
      receivers.add(receiver);
    }
    this.expandedReceivers.set(receivers);
  }

  addRecipient(): void {
    const notificationRecipients = this.form().notificationRecipients;
    notificationRecipients().value.update((recipients) => [...recipients, createEmptyNotificationRecipient()]);
    const addedReceiver = notificationRecipients[notificationRecipients.length - 1];
    this.expandedReceivers.update((receivers) => new Set(receivers).add(addedReceiver));
  }

  deleteRecipient(receiver: TNotificationRecipientField): void {
    const { name, email } = receiver().value();
    if (!name || !email) {
      this.#removeRecipient(receiver);
      return;
    }

    const data: TConfirmationModalData = {
      svgIcon: 'trash-bin-minimalistic-outline',
      iconVariant: 'danger',
      title: 'SIGNING_PARTICIPANT.DELETE_RECEIVER_TITLE',
      subtitle: 'SIGNING_PARTICIPANT.DELETE_SUBTITLE',
      message: 'SIGNING_PARTICIPANT.DELETE_RECEIVER_DESCRIPTION',
      primaryButtonLabel: 'SIGNING_PARTICIPANT.REMOVE_RECEIVER',
      primaryButtonColor: 'danger',
      secondaryButtonLabel: 'GENERAL.CANCEL',
      participant: { name, email },
    };
    this.#modalService.openAbstractModal({
      component: ConfirmationModalComponent,
      dialogData: data,
      onSubmit: () => this.#removeRecipient(receiver),
    });
  }

  #removeRecipient(receiver: TNotificationRecipientField): void {
    const removedReceiver = receiver().value();
    this.form()
      .notificationRecipients()
      .value.update((recipients) => recipients.filter((recipient) => recipient !== removedReceiver));
  }
}
